import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import SearchBar from '@/components/feature/SearchBar';

const NAV_ITEMS = [
  { label: '首页', to: '/' },
  { label: '电影', to: '/category?type=movie' },
  { label: '电视剧', to: '/category?type=tv' },
  { label: '资讯', to: '/news' },
  { label: '关于我们', to: '/about' },
];

function isItemActive(pathname: string, search: string, to: string): boolean {
  const [path, query] = to.split('?');
  if (pathname !== path) return false;
  if (!query) return true;
  const type = new URLSearchParams(query).get('type');
  const current = new URLSearchParams(search).get('type') ?? 'movie';
  return type === current;
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen || searchOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [drawerOpen, searchOpen]);

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors ${
        scrolled
          ? 'border-background-800 bg-background-950/95 backdrop-blur'
          : 'border-transparent bg-background-950/80 backdrop-blur'
      }`}
    >
      <div className="mx-auto flex h-16 w-full items-center gap-4 px-4 md:px-6">
        <Link to="/" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-950">
            <i className="ri-leaf-fill text-lg" />
          </span>
          <span className="font-heading text-lg font-bold tracking-tight text-foreground-50">
            嫩草影院
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={() =>
                `whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-medium transition-colors ${
                  isItemActive(location.pathname, location.search, item.to)
                    ? 'bg-primary-500/15 text-primary-400'
                    : 'text-foreground-300 hover:bg-background-800 hover:text-foreground-50'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto hidden w-72 xl:block">
          <SearchBar />
        </div>

        <div className="ml-auto flex items-center gap-2 xl:ml-0">
          <button
            type="button"
            aria-label="搜索"
            onClick={() => setSearchOpen(true)}
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-200 transition-colors hover:bg-background-800 xl:hidden"
          >
            <i className="ri-search-line text-xl" />
          </button>
          <button
            type="button"
            aria-label="菜单"
            onClick={() => setDrawerOpen(true)}
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-200 transition-colors hover:bg-background-800 lg:hidden"
          >
            <i className="ri-menu-line text-2xl" />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col bg-background-950/98 px-4 pt-4 lg:hidden">
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label="关闭搜索"
              onClick={() => setSearchOpen(false)}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-foreground-200 hover:bg-background-800"
            >
              <i className="ri-arrow-left-line text-2xl" />
            </button>
            <SearchBar size="md" autoFocus onSubmitted={() => setSearchOpen(false)} />
          </div>
          <p className="mt-6 text-sm text-foreground-500">输入影视名称，快速找到你想看的内容。</p>
        </div>
      )}

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside className="absolute right-0 top-0 flex h-full w-72 max-w-[80vw] flex-col bg-background-900 shadow-xl">
            <div className="flex h-16 items-center justify-between border-b border-background-800 px-4">
              <span className="font-heading text-base font-bold text-foreground-50">导航</span>
              <button
                type="button"
                aria-label="关闭菜单"
                onClick={() => setDrawerOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-200 hover:bg-background-800"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>
            <nav className="flex flex-col gap-1 p-3">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-3 rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                    isItemActive(location.pathname, location.search, item.to)
                      ? 'bg-primary-500/15 text-primary-400'
                      : 'text-foreground-200 hover:bg-background-800'
                  }`}
                >
                  <i className="ri-arrow-right-s-line text-xl" />
                  {item.label}
                </Link>
              ))}
            </nav>
            <div className="mt-auto border-t border-background-800 p-4">
              <p className="text-xs leading-relaxed text-foreground-500">
                嫩草影院 · 最新电影电视剧在线播放与资讯
              </p>
            </div>
          </aside>
        </div>
      )}
    </header>
  );
}